import { $prose } from '@milkdown/utils';
import { DOMSerializer, Fragment, Mark, type Node as ProseMirrorNode } from 'prosemirror-model';
import { Plugin, PluginKey, type EditorState } from 'prosemirror-state';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';

interface ReadableDiffPluginState {
  baselineDoc: ProseMirrorNode | null;
  decorations: DecorationSet;
}

interface SetReadableDiff {
  baselineDoc: ProseMirrorNode | null;
}

interface ChildReference {
  node: ProseMirrorNode;
  position: number;
}

interface TextToken {
  key: string;
  text: string;
  node: ProseMirrorNode;
  from: number;
  to: number;
}

type Match = readonly [number, number];

const readableDiffPluginKey = new PluginKey<ReadableDiffPluginState>('readableDiff');
const maximumLcsCells = 250_000;

export function createReadableDiffPlugin(onClearedByDocumentChange: () => void) {
  return $prose(
    () =>
      new Plugin<ReadableDiffPluginState>({
        key: readableDiffPluginKey,
        state: {
          init: (_, state) => inactiveState(state),
          apply(transaction, diffState, _, newState) {
            const update = transaction.getMeta(readableDiffPluginKey) as SetReadableDiff | undefined;
            if (update != null) {
              return update.baselineDoc == null
                ? inactiveState(newState)
                : activeState(update.baselineDoc, newState.doc);
            }

            if (transaction.docChanged && diffState.baselineDoc != null) {
              return inactiveState(newState);
            }

            return diffState;
          }
        },
        props: {
          decorations(state) {
            return readableDiffPluginKey.getState(state)?.decorations ?? null;
          }
        },
        view() {
          return {
            update(view, previousState) {
              const previousDiff = readableDiffPluginKey.getState(previousState);
              const currentDiff = readableDiffPluginKey.getState(view.state);
              if (previousState.doc !== view.state.doc
                && previousDiff?.baselineDoc != null
                && currentDiff?.baselineDoc == null) {
                onClearedByDocumentChange();
              }
            }
          };
        }
      })
  );
}

export function showReadableDiff(view: EditorView, baselineDoc: ProseMirrorNode): void {
  view.dispatch(view.state.tr.setMeta(readableDiffPluginKey, { baselineDoc } satisfies SetReadableDiff));
}

export function hideReadableDiff(view: EditorView): void {
  if (readableDiffPluginKey.getState(view.state)?.baselineDoc == null) {
    return;
  }

  view.dispatch(view.state.tr.setMeta(readableDiffPluginKey, { baselineDoc: null } satisfies SetReadableDiff));
}

function inactiveState(state: EditorState): ReadableDiffPluginState {
  return {
    baselineDoc: null,
    decorations: DecorationSet.empty
  };
}

function activeState(baselineDoc: ProseMirrorNode, currentDoc: ProseMirrorNode): ReadableDiffPluginState {
  const decorations: Decoration[] = [];
  const keyCounter = { value: 0 };
  diffChildren(baselineDoc, currentDoc, 0, decorations, keyCounter);
  return {
    baselineDoc,
    decorations: DecorationSet.create(currentDoc, decorations)
  };
}

function diffChildren(
  baselineParent: ProseMirrorNode,
  currentParent: ProseMirrorNode,
  currentContentStart: number,
  decorations: Decoration[],
  keyCounter: { value: number }
): void {
  const baselineChildren = childReferences(baselineParent, 0);
  const currentChildren = childReferences(currentParent, currentContentStart);
  const matches = findMatches(
    baselineChildren,
    currentChildren,
    (baseline, current) => readableNodesEqual(baseline.node, current.node)
  );
  let baselineIndex = 0;
  let currentIndex = 0;

  for (const [matchedBaselineIndex, matchedCurrentIndex] of [...matches, [baselineChildren.length, currentChildren.length] as const]) {
    const insertionPosition = currentChildren[currentIndex]?.position
      ?? currentChildren[matchedCurrentIndex]?.position
      ?? currentContentStart + currentParent.content.size;
    diffChangedChildren(
      baselineChildren.slice(baselineIndex, matchedBaselineIndex),
      currentChildren.slice(currentIndex, matchedCurrentIndex),
      insertionPosition,
      decorations,
      keyCounter
    );
    baselineIndex = matchedBaselineIndex + 1;
    currentIndex = matchedCurrentIndex + 1;
  }
}

function diffChangedChildren(
  baselineChildren: ChildReference[],
  currentChildren: ChildReference[],
  insertionPosition: number,
  decorations: Decoration[],
  keyCounter: { value: number }
): void {
  if (baselineChildren.length === currentChildren.length
    && baselineChildren.every((baseline, index) => compatibleNodes(baseline.node, currentChildren[index]!.node))) {
    baselineChildren.forEach((baseline, index) => {
      diffCompatibleNode(baseline.node, currentChildren[index]!, decorations, keyCounter);
    });
    return;
  }

  for (const baseline of baselineChildren) {
    decorations.push(deletedNodeWidget(insertionPosition, baseline.node, keyCounter));
  }

  for (const current of currentChildren) {
    decorateAddedNode(current, decorations);
  }
}

function compatibleNodes(baseline: ProseMirrorNode, current: ProseMirrorNode): boolean {
  return readableNodesHaveSameMarkup(baseline, current) && !baseline.isLeaf && !current.isLeaf;
}

export function readableNodesEqual(baseline: ProseMirrorNode, current: ProseMirrorNode): boolean {
  if (!readableNodesHaveSameMarkup(baseline, current)) {
    return false;
  }

  if (baseline.isText || current.isText) {
    return baseline.text === current.text;
  }

  if (baseline.childCount !== current.childCount) {
    return false;
  }

  for (let index = 0; index < baseline.childCount; index++) {
    if (!readableNodesEqual(baseline.child(index), current.child(index))) {
      return false;
    }
  }

  return true;
}

export function readableNodesHaveSameMarkup(baseline: ProseMirrorNode, current: ProseMirrorNode): boolean {
  if (baseline.type !== current.type || !Mark.sameSet(baseline.marks, current.marks)) {
    return false;
  }

  if (baseline.type.name === 'heading') {
    return baseline.attrs.level === current.attrs.level;
  }

  return baseline.sameMarkup(current);
}

function diffCompatibleNode(
  baseline: ProseMirrorNode,
  current: ChildReference,
  decorations: Decoration[],
  keyCounter: { value: number }
): void {
  if (baseline.isTextblock && current.node.isTextblock) {
    if (hasNonTextLeaf(baseline) || hasNonTextLeaf(current.node)) {
      decorations.push(deletedNodeWidget(current.position, baseline, keyCounter));
      decorateAddedNode(current, decorations);
      return;
    }

    diffTextblock(baseline, current, decorations, keyCounter);
    return;
  }

  diffChildren(baseline, current.node, current.position + 1, decorations, keyCounter);
}

function diffTextblock(
  baseline: ProseMirrorNode,
  current: ChildReference,
  decorations: Decoration[],
  keyCounter: { value: number }
): void {
  const baselineTokens = textTokens(baseline, 0);
  const currentTokens = textTokens(current.node, current.position + 1);
  const matches = findMatches(baselineTokens, currentTokens, (left, right) => left.key === right.key);
  let baselineIndex = 0;
  let currentIndex = 0;

  for (const [matchedBaselineIndex, matchedCurrentIndex] of [...matches, [baselineTokens.length, currentTokens.length] as const]) {
    const addedTokens = currentTokens.slice(currentIndex, matchedCurrentIndex);
    for (const token of addedTokens) {
      if (token.from < token.to) {
        decorations.push(Decoration.inline(token.from, token.to, { class: 'readable-diff-added' }));
      }
    }

    const removedTokens = baselineTokens.slice(baselineIndex, matchedBaselineIndex);
    if (removedTokens.length > 0) {
      const insertionPosition = currentTokens[currentIndex]?.from
        ?? currentTokens[matchedCurrentIndex]?.from
        ?? current.position + current.node.nodeSize - 1;
      decorations.push(deletedTokensWidget(insertionPosition, removedTokens, keyCounter));
    }

    baselineIndex = matchedBaselineIndex + 1;
    currentIndex = matchedCurrentIndex + 1;
  }
}

function childReferences(parent: ProseMirrorNode, contentStart: number): ChildReference[] {
  const children: ChildReference[] = [];
  let offset = 0;
  parent.forEach(node => {
    children.push({ node, position: contentStart + offset });
    offset += node.nodeSize;
  });
  return children;
}

function textTokens(node: ProseMirrorNode, contentStart: number): TextToken[] {
  const tokens: TextToken[] = [];
  node.descendants((child, position) => {
    if (!child.isText || child.text == null) {
      return true;
    }

    let offset = 0;
    for (const part of child.text.match(/\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu) ?? []) {
      const from = contentStart + position + offset;
      const markKey = JSON.stringify(child.marks.map(mark => mark.toJSON()));
      tokens.push({
        key: `${part}\u0000${markKey}`,
        text: part,
        node: child,
        from,
        to: from + part.length
      });
      offset += part.length;
    }

    return false;
  });
  return tokens;
}

function hasNonTextLeaf(node: ProseMirrorNode): boolean {
  let found = false;
  node.descendants(child => {
    if (child.isLeaf && !child.isText) {
      found = true;
      return false;
    }

    return !found;
  });
  return found;
}

function decorateAddedNode(reference: ChildReference, decorations: Decoration[]): void {
  if (reference.node.isText) {
    decorations.push(Decoration.inline(
      reference.position,
      reference.position + reference.node.nodeSize,
      { class: 'readable-diff-added' }
    ));
    return;
  }

  decorations.push(Decoration.node(
    reference.position,
    reference.position + reference.node.nodeSize,
    { class: 'readable-diff-added readable-diff-added-block' }
  ));
}

function deletedNodeWidget(
  position: number,
  node: ProseMirrorNode,
  keyCounter: { value: number }
): Decoration {
  return Decoration.widget(position, () => {
    const serialized = DOMSerializer.fromSchema(node.type.schema).serializeNode(node, { document });
    const element = serialized instanceof HTMLElement ? serialized : document.createElement('span');
    if (!(serialized instanceof HTMLElement)) {
      element.append(serialized);
    }

    element.classList.add('readable-diff-removed', 'readable-diff-removed-block');
    element.contentEditable = 'false';
    element.dataset.readableDiff = 'removed';
    return element;
  }, {
    side: -1,
    ignoreSelection: true,
    stopEvent: () => true,
    key: `readable-diff-removed-node-${keyCounter.value++}`
  });
}

function deletedTokensWidget(
  position: number,
  tokens: TextToken[],
  keyCounter: { value: number }
): Decoration {
  return Decoration.widget(position, () => {
    const wrapper = document.createElement('span');
    const nodes = tokens.map(token => token.node.type.schema.text(token.text, token.node.marks));
    wrapper.append(DOMSerializer.fromSchema(tokens[0]!.node.type.schema).serializeFragment(
      Fragment.fromArray(nodes),
      { document }
    ));
    wrapper.className = 'readable-diff-removed';
    wrapper.contentEditable = 'false';
    wrapper.dataset.readableDiff = 'removed';
    return wrapper;
  }, {
    side: -1,
    ignoreSelection: true,
    stopEvent: () => true,
    key: `readable-diff-removed-text-${keyCounter.value++}`
  });
}

function findMatches<T>(left: T[], right: T[], equal: (left: T, right: T) => boolean): Match[] {
  if (left.length === 0 || right.length === 0) {
    return [];
  }

  if (left.length * right.length > maximumLcsCells) {
    return edgeMatches(left, right, equal);
  }

  const lengths = Array.from(
    { length: left.length + 1 },
    () => new Uint32Array(right.length + 1)
  );
  for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex--) {
    for (let rightIndex = right.length - 1; rightIndex >= 0; rightIndex--) {
      lengths[leftIndex]![rightIndex] = equal(left[leftIndex]!, right[rightIndex]!)
        ? lengths[leftIndex + 1]![rightIndex + 1]! + 1
        : Math.max(lengths[leftIndex + 1]![rightIndex]!, lengths[leftIndex]![rightIndex + 1]!);
    }
  }

  const matches: Match[] = [];
  let leftIndex = 0;
  let rightIndex = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    if (equal(left[leftIndex]!, right[rightIndex]!)) {
      matches.push([leftIndex, rightIndex]);
      leftIndex++;
      rightIndex++;
    } else if (lengths[leftIndex + 1]![rightIndex]! >= lengths[leftIndex]![rightIndex + 1]!) {
      leftIndex++;
    } else {
      rightIndex++;
    }
  }

  return matches;
}

function edgeMatches<T>(left: T[], right: T[], equal: (left: T, right: T) => boolean): Match[] {
  const matches: Match[] = [];
  let prefix = 0;
  while (prefix < left.length && prefix < right.length && equal(left[prefix]!, right[prefix]!)) {
    matches.push([prefix, prefix]);
    prefix++;
  }

  const suffix: Match[] = [];
  let leftIndex = left.length - 1;
  let rightIndex = right.length - 1;
  while (leftIndex >= prefix && rightIndex >= prefix && equal(left[leftIndex]!, right[rightIndex]!)) {
    suffix.push([leftIndex, rightIndex]);
    leftIndex--;
    rightIndex--;
  }

  return matches.concat(suffix.reverse());
}
